import { Button, Tag, Text } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { ViewSubSection } from "shared/components/app";
import { Flex } from "styled-system/jsx";
import { useTimeline, UseTimelineProps } from "./useTimeline";
import { TimelineGroup } from "../../components";
import { SimpleFlag, SimpleLoader } from "shared/components/atoms";
import { Collapsible, NoteCard } from "shared/components/molecules";
import { Variants } from "framer-motion";
import { EmptyState } from "shared/components/root";

type TimelineProps = UseTimelineProps & {
  onOpenLeadModal: (leadId: string) => void;
  onOpenBroadcastModal: (broadcastId: string, timelineItemId: string) => void;
  onOpenFormResponseModal: (surveyResponseId: string) => void;
  onOpenEventModal: (eventId: string) => void;
  loadingTimelineItemId: string;
};

export const Timeline = ({
  person,
  onOpenLeadModal,
  onOpenBroadcastModal,
  onOpenFormResponseModal,
  onOpenEventModal,
  loadingTimelineItemId,
}: TimelineProps) => {
  const { t } = useTranslation("people", {
    keyPrefix: "PERSON_DETAILS_MODAL.TIMELINE",
  });

  const {
    filterOptions,
    selectedFilter,
    timelineGroups,
    filteredTimelineGroups,
    loading,
    changeLogError,
    notesError,
    activityLogsError,
    handleFilterClick,
    isAddingNote,
    setIsAddingNote,
    handleNoteSaveSuccess,
  } = useTimeline({
    person,
  });

  return (
    <ViewSubSection
      title="Timeline"
      buttonOverride={
        <Button
          visual="unstyled"
          fontSize="16px"
          fontWeight="500"
          color="text.accent.blue.mid"
          _hover={{
            color: "text.accent.blue.dark",
          }}
          _focusVisible={{
            color: "text.accent.blue.dark",
          }}
          onClick={() => setIsAddingNote(true)}
        >
          {t("CTA_BUTTON_COPY")}
        </Button>
      }
    >
      {/* Filters */}
      <Flex alignItems="center" gap="100" flexWrap="wrap">
        <Text visual="smallTag" flexShrink={0}>
          {t("ONLY_SHOW.TITLE")}
        </Text>
        <Flex alignItems="center" gap="75" flexWrap="wrap">
          {filterOptions.map(filter => (
            <Tag
              key={filter.value}
              visual="primary"
              mode={selectedFilter === filter.value ? "dark" : "light"}
              onClick={() => handleFilterClick(filter.value)}
            >
              {filter.label}
            </Tag>
          ))}
        </Flex>
      </Flex>

      {loading && <SimpleLoader />}
      {changeLogError && (
        <SimpleFlag visual="destructive" text={t("MESSAGES.ERROR_ACTIVITY")} />
      )}
      {notesError && (
        <SimpleFlag visual="destructive" text={t("MESSAGES.ERROR_NOTES")} />
      )}
      {activityLogsError && (
        <SimpleFlag
          visual="destructive"
          text={t("MESSAGES.ERROR_BROADCASTS")}
        />
      )}

      {!loading && timelineGroups.length === 0 && (
        <EmptyState text={t("MESSAGES.NO_DATA")} />
      )}
      {!loading &&
        timelineGroups.length > 0 &&
        filteredTimelineGroups.length === 0 && (
          <EmptyState text={t("MESSAGES.NO_DATA_FOR_FILTER")} />
        )}

      {/* Add note */}
      <Collapsible
        open={isAddingNote}
        additionalVariantProps={
          {
            open: {
              display: "block",
            },
            closed: {
              display: "none",
            },
          } as Variants
        }
      >
        {isAddingNote && (
          <NoteCard
            initialMode={isAddingNote ? "edit" : "view"}
            onSaveSuccess={handleNoteSaveSuccess}
            onCancel={() => setIsAddingNote(false)}
          />
        )}
      </Collapsible>

      {/* Timeline Items */}
      {!loading &&
        filteredTimelineGroups.length > 0 &&
        filteredTimelineGroups.map(group => (
          <TimelineGroup
            key={group.groupByDate}
            timelineGroup={group}
            onOpenLeadModal={onOpenLeadModal}
            onOpenBroadcastModal={onOpenBroadcastModal}
            onOpenFormResponseModal={onOpenFormResponseModal}
            onOpenEventModal={onOpenEventModal}
            loadingTimelineItemId={loadingTimelineItemId}
          />
        ))}
    </ViewSubSection>
  );
};
